import React, {
    PropsWithChildren,
    useContext,
    useEffect,
    useState,
} from "react";
import PageHeader from "../../../../../components/PageHeader/PageHeader";
import {
    Config,
    ExperimentationTestStatus,
    Pagetype,
} from "../../../../../interfaces/Config";
import { useUrl } from "../../../../../hooks/usePage";
import { defineMessages, useIntl } from "react-intl";
import { pageTitles } from "../../../../../utils/messages";
import RouteWithHeaderWrapper from "../../../../../components/RouteWithHeaderWrapper/RouteWithHeaderWrapper";
import TabTitle from "../../../../../components/TabTitle/TabTitle";
import {
    WorkmodeGrid,
    WorkmodePage,
} from "../../../../../design-system-components/WorkmodeLayout";
import { useExperimentationQuery } from "../../api/useQueries";

import "./TeacherExperimentationModeList.scss";
import TestCard from "../../TestCard/TestCard";
import { Chip } from "@evidenceb/athena-common/design-system/Chip";
import ProgressBar from "../../../../../design-system-components/ProgressBar/ProgressBar";
import Select from "../../../../../components/Select/Select";
import Avatar from "../../../../../components/Avatar/Avatar";
import Loader from "../../../../../components/Loader/Loader";
import {
    Loader as TestLoader,
    LoaderStatus,
} from "../../../../../interfaces/Status";
import SimpleBar from "simplebar-react";
import { configStore } from "../../../../../contexts/ConfigContext";

import {
    flattenPool,
    getDisabledDescription,
    isDisabled,
    isTestEnabled,
} from "../../utils";
import useTheme from "../../../../../hooks/useTheme";
import { useActiveSubject } from "../../../../../hooks/useActiveSubject";
import EmptyContainer from "../../../../../design-system-components/WorkmodeLayout/EmptyContainer/EmptyContainer";
import { useImages } from "../../../../../hooks/useConfig";
import { defaultImages } from "../../../../../utils/defaultImages";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import BottomSheet from "../../../../../components/BottomSheet/BottomSheet";
import { useOverlayTriggerState } from "react-stately";
import { LearningSet } from "../../../../../interfaces/AthenaResources";
import { ClassroomsProgress, Experimentation } from "../../type";
import useTeacherTests from "./useTeacherTests";

const TeacherExperimentationModeList = () => {
    // const { isTabletOrSmaller } = useBreakpoints();
    // use the same as in Students' ExperimentationList.tsx here?
    // But there may be multiple classrooms, so we need to get the learning sets for each

    const tests = useTeacherTests();

    const intl = useIntl();

    const { isTabletOrSmaller } = useBreakpoints();
    const bottomSheetState = useOverlayTriggerState({});

    const [testInPreviewId, setTestInPreviewId] = useState<string>();
    const { data: testInPreview } = useExperimentationQuery(
        testInPreviewId ?? ""
    );

    const updatePreview = (id: string) => {
        if (!isTabletOrSmaller && testInPreviewId === id) {
            setTestInPreviewId(undefined);
        } else {
            setTestInPreviewId(id);
        }
        if (isTabletOrSmaller) bottomSheetState.open();
    };

    const images = useImages();

    const {
        config: { mode },
    } = useContext(configStore);
    const theme = useTheme();
    const activeSubject = useActiveSubject();

    const preTests = mode?.test?.pre && flattenPool(mode.test.pre);
    const postTests = mode?.test?.post && flattenPool(mode.test.post);
    const preTestsStatus = mode?.test?.preStatus;
    const postTestsStatus = mode?.test?.postStatus;
    const isPreDisabled = isDisabled(preTestsStatus) ?? false;
    const isPostDisabled = isDisabled(postTestsStatus) ?? false;

    useEffect(() => {
        if (
            !isTabletOrSmaller &&
            !testInPreviewId &&
            tests.status === LoaderStatus.Success
        ) {
            if (
                preTestsStatus === ExperimentationTestStatus.ENABLED &&
                preTests?.length
            ) {
                const activePreTest = tests.payload.find(
                    (l) => l.data.learning_set_id === preTests[0]
                );
                if (
                    preTests &&
                    activePreTest?.data.learning_set_id &&
                    mode?.test &&
                    isTestEnabled(
                        activePreTest.data.learning_set_id,
                        mode?.test
                    )
                )
                    setTestInPreviewId(activePreTest.data.learning_set_id);
            }
            if (
                postTestsStatus === ExperimentationTestStatus.ENABLED &&
                postTests?.length
            ) {
                const test = tests.payload.find(
                    (l) => l.data.learning_set_id === postTests[0]
                );
                if (
                    test &&
                    mode?.test &&
                    isTestEnabled(test.data.learning_set_id, mode.test)
                )
                    setTestInPreviewId(test.data.learning_set_id);
            }
        }
    }, [
        isTabletOrSmaller,
        tests,
        mode?.test,
        postTests,
        postTestsStatus,
        preTests,
        preTestsStatus,
        testInPreviewId,
    ]);
    return (
        <Wrapper>
            <WorkmodeGrid>
                <WorkmodeGrid.LeftColumn id="teacher-experimentation-mode-list__left-column">
                    {tests.status === LoaderStatus.Error && (
                        <p>
                            {intl.formatMessage(
                                messages.experimentationListError
                            )}
                        </p>
                    )}

                    <Loader isLoading={tests.status === LoaderStatus.Loading}>
                        {tests.status === LoaderStatus.Success && (
                            <SimpleBar style={{ height: "100%" }}>
                                {preTests && preTests.length > 0 && (
                                    <>
                                        <h3 className="test-title">
                                            {intl.formatMessage(
                                                messages.preTests
                                            )}
                                        </h3>
                                        <ul>
                                            {tests.payload
                                                .filter(
                                                    (test) => test.isPretest
                                                )
                                                .map((test) => {
                                                    const averageProgress =
                                                        computeAverageProgress(
                                                            test
                                                        );
                                                    return (
                                                        <li
                                                            key={
                                                                test.data
                                                                    .learning_set_id
                                                            }
                                                        >
                                                            <TestCard
                                                                clickAction={{
                                                                    type: "actionableButton",
                                                                    onPress:
                                                                        () => {
                                                                            updatePreview(
                                                                                test
                                                                                    .data
                                                                                    .learning_set_id
                                                                            );
                                                                        },
                                                                    label: "Preview",
                                                                }}
                                                                test={test.data}
                                                                averageProgress={
                                                                    averageProgress
                                                                }
                                                                isTeacher={true}
                                                                isSelected={
                                                                    testInPreviewId ===
                                                                    test.data
                                                                        .learning_set_id
                                                                }
                                                                isDisabled={
                                                                    isPreDisabled
                                                                }
                                                                disabledDescription={
                                                                    isPreDisabled
                                                                        ? getDisabledDescription(
                                                                              preTestsStatus,
                                                                              activeSubject
                                                                                  .colors
                                                                                  .default,
                                                                              theme,
                                                                              intl
                                                                          )
                                                                        : undefined
                                                                }
                                                            />
                                                        </li>
                                                    );
                                                })}
                                        </ul>
                                    </>
                                )}
                                {postTests && postTests.length > 0 && (
                                    <>
                                        <h3 className="test-title">
                                            {intl.formatMessage(
                                                messages.postTests
                                            )}
                                        </h3>
                                        <ul>
                                            {tests.payload
                                                .filter(
                                                    (test) => !test.isPretest
                                                )
                                                .map((test) => {
                                                    const averageProgress =
                                                        computeAverageProgress(
                                                            test
                                                        );
                                                    return (
                                                        <li
                                                            key={
                                                                test.data
                                                                    .learning_set_id
                                                            }
                                                        >
                                                            <TestCard
                                                                clickAction={{
                                                                    type: "actionableButton",
                                                                    onPress:
                                                                        () => {
                                                                            updatePreview(
                                                                                test
                                                                                    .data
                                                                                    .learning_set_id
                                                                            );
                                                                        },
                                                                    label: "Preview",
                                                                }}
                                                                test={test.data}
                                                                averageProgress={
                                                                    averageProgress
                                                                }
                                                                isTeacher={true}
                                                                isSelected={
                                                                    testInPreviewId ===
                                                                    test.data
                                                                        .learning_set_id
                                                                }
                                                                isDisabled={
                                                                    isPostDisabled
                                                                }
                                                                disabledDescription={
                                                                    isPostDisabled
                                                                        ? getDisabledDescription(
                                                                              postTestsStatus,
                                                                              activeSubject
                                                                                  .colors
                                                                                  .default,
                                                                              theme,
                                                                              intl
                                                                          )
                                                                        : undefined
                                                                }
                                                            />
                                                        </li>
                                                    );
                                                })}
                                        </ul>
                                    </>
                                )}
                            </SimpleBar>
                        )}
                    </Loader>
                </WorkmodeGrid.LeftColumn>

                <WorkmodeGrid.RightColumn className="teacher-experimentation-mode-list__side-container">
                    <RightColumnContent
                        tests={tests}
                        testInPreview={testInPreview}
                        preTestsStatus={preTestsStatus}
                        postTestsStatus={postTestsStatus}
                        images={images}
                    />
                </WorkmodeGrid.RightColumn>
            </WorkmodeGrid>
            {isTabletOrSmaller && (
                <BottomSheet
                    state={bottomSheetState}
                    onClose={() => setTestInPreviewId(undefined)}
                >
                    <RightColumnContent
                        tests={tests}
                        testInPreview={testInPreview}
                        preTestsStatus={preTestsStatus}
                        postTestsStatus={postTestsStatus}
                        images={images}
                    />
                </BottomSheet>
            )}
        </Wrapper>
    );
};
export default TeacherExperimentationModeList;

interface RightColumnContentProps {
    tests: TestLoader<Experimentation[]>;
    testInPreview:
        | LearningSet
        | {
              [key: string]: LearningSet;
          }
        | undefined;
    preTestsStatus: ExperimentationTestStatus | undefined;
    postTestsStatus: ExperimentationTestStatus | undefined;
    images: Config["features"]["images"];
}

const RightColumnContent = ({
    tests,
    testInPreview,
    preTestsStatus,
    postTestsStatus,
    images,
}: RightColumnContentProps) => {
    const intl = useIntl();
    return (
        <Loader isLoading={tests.status === LoaderStatus.Loading}>
            {tests.status === LoaderStatus.Success && (
                <>
                    {testInPreview && (
                        <>
                            {Array.isArray(testInPreview.learning_sets) &&
                                testInPreview.learning_sets.length > 0 && (
                                    <TestPreview
                                        testInPreview={testInPreview}
                                        tests={tests.payload}
                                    />
                                )}
                        </>
                    )}
                    {preTestsStatus === ExperimentationTestStatus.INCOMING &&
                        postTestsStatus ===
                            ExperimentationTestStatus.INCOMING && (
                            <EmptyContainer
                                className="teacher-experimentation-mode-list__test-message"
                                img={
                                    images?.resources?.teacher?.tests
                                        ?.emptyState
                                }
                                defaultImg={defaultImages.experimentationClosed}
                            >
                                <p>
                                    {intl.formatMessage(
                                        messages.experimentationTestsIncoming
                                    )}
                                </p>
                            </EmptyContainer>
                        )}
                    {preTestsStatus === ExperimentationTestStatus.CLOSED &&
                        postTestsStatus ===
                            ExperimentationTestStatus.INCOMING && (
                            <EmptyContainer
                                className="teacher-experimentation-mode-list__test-message"
                                img={
                                    images?.resources?.teacher?.tests
                                        ?.emptyState
                                }
                                defaultImg={defaultImages.experimentationClosed}
                            >
                                <p>
                                    {intl.formatMessage(
                                        messages.experimentationPreTestsClosed
                                    )}
                                </p>
                            </EmptyContainer>
                        )}
                    {preTestsStatus === ExperimentationTestStatus.CLOSED &&
                        postTestsStatus ===
                            ExperimentationTestStatus.CLOSED && (
                            <EmptyContainer
                                className="teacher-experimentation-mode-list__test-message"
                                img={images?.notifications?.emptyState}
                                defaultImg={defaultImages.experimentationClosed}
                            >
                                <p>
                                    {intl.formatMessage(
                                        messages.experimentationAllTestsAreClosed
                                    )}
                                </p>
                            </EmptyContainer>
                        )}
                </>
            )}
        </Loader>
    );
};

const computeAverageProgress = (test: any) => {
    if (!test.classroomsProgress) return 0;

    const studentNumber = test.classroomsProgress.reduce(
        (sum: number, classroomProgress: any) => {
            return sum + classroomProgress.students_progress.length;
        },
        0
    );

    const sumExercises = test.classroomsProgress.reduce(
        (sum1: number, classroomProgress: any) => {
            return (
                sum1 +
                classroomProgress.students_progress.reduce(
                    (sum: number, studentProgress: any) => {
                        return sum + studentProgress.exercises_done;
                    },
                    0
                )
            );
        },
        0
    );

    return studentNumber > 0 ? sumExercises / studentNumber : 0;
};

const Wrapper = ({ children }: PropsWithChildren) => {
    const intl = useIntl();
    const title = intl.formatMessage(pageTitles.teacherExperimentationsList);
    const workModeSelectionUrl = useUrl(Pagetype.WORK_MODES);
    return (
        <>
            <TabTitle title={title} />
            <RouteWithHeaderWrapper routerClassName="teacher-experimentation-mode-list__router">
                <WorkmodePage>
                    <PageHeader
                        title={{ content: title }}
                        backTo={workModeSelectionUrl}
                    />
                    {children}
                </WorkmodePage>
            </RouteWithHeaderWrapper>
        </>
    );
};

const TestPreview = ({
    testInPreview,
    tests,
}: {
    testInPreview: any;
    tests: any;
}) => {
    const intl = useIntl();
    const [classroomId, setClassroomId] = useState<string>("");
    const learningSet: any = testInPreview.learning_sets[0];
    const classroomsProgress = tests.find((test: any) => {
        return test.data.learning_set_id === testInPreview.learning_set_id;
    })?.classroomsProgress;

    const itemNumber = learningSet.learning_items.length;
    const currentClassroomProgress = classroomsProgress.find(
        (classroomProgress: any) => classroomProgress.id === classroomId
    );

    return (
        <div className="test-preview">
            <div>
                <h2 className="test-name">{testInPreview.name}</h2>
                <Chip
                    input={intl.formatMessage(
                        messages.experimentationPreviewExercises,
                        {
                            count: itemNumber,
                        }
                    )}
                />
                <p className="test-description">{testInPreview.description}</p>
            </div>

            <div className="classrooms-select-container">
                <h3>
                    {intl.formatMessage(
                        messages.experimentationPreviewProgressions
                    )}
                </h3>
                {classroomsProgress.length > 1 && (
                    <Select
                        label={intl.formatMessage(messages.selectClassroom)}
                        className="classrooms-select"
                        onSelectOption={(classroomId: string) => {
                            setClassroomId(classroomId);
                        }}
                        selectedOption={
                            currentClassroomProgress
                                ? currentClassroomProgress.name
                                : intl.formatMessage(messages.selectClassroom)
                        }
                    >
                        <option key={"allClasses"} value={""}>
                            {intl.formatMessage(messages.selectClassroom)}
                        </option>
                        {classroomsProgress.map(
                            (classroomProgress: any, i: number) => (
                                <option key={i} value={classroomProgress.id}>
                                    {`${classroomProgress.name}`}
                                </option>
                            )
                        )}
                    </Select>
                )}
            </div>

            {currentClassroomProgress ? (
                <ClassroomTestProgress
                    key={currentClassroomProgress.id}
                    classroomProgress={currentClassroomProgress}
                    showSeparator={false}
                    itemNumber={itemNumber}
                />
            ) : (
                classroomsProgress.map(
                    (classroomProgress: ClassroomsProgress, index: number) => {
                        return (
                            <ClassroomTestProgress
                                key={classroomProgress.id}
                                classroomProgress={classroomProgress}
                                showSeparator={index > 0}
                                itemNumber={itemNumber}
                            />
                        );
                    }
                )
            )}
        </div>
    );
};

const ClassroomTestProgress = ({
    classroomProgress,
    showSeparator,
    itemNumber,
}: {
    classroomProgress: ClassroomsProgress;
    showSeparator: boolean;
    itemNumber: number;
}) => {
    return (
        <div key={classroomProgress.id} className={"classroom-progress"}>
            {showSeparator ? <hr className="separator" /> : ""}
            <h4 className="classroom-name">{classroomProgress.name}</h4>
            <ul>
                {classroomProgress.students_progress.map(
                    (studentProgress: any) => {
                        /*
                        We don't want to show a proportion of exercises done higher than 1, but it happens 
                        when the number of exercises in the test has been modified after the students have
                        completed it. In this case, we show the number of exercises in the test instead of
                        the number of exercises done.
                        */
                        const saneValue = Math.min(
                            studentProgress.exercises_done,
                            itemNumber
                        );
                        return (
                            <li
                                key={studentProgress.id}
                                className={"student-progress"}
                            >
                                <div
                                    className={"student-progress__student-name"}
                                >
                                    <Avatar
                                        name={`${studentProgress.firfirst_namestname} ${studentProgress.last_name}`}
                                    />
                                    {`${studentProgress.first_name} ${studentProgress.last_name}`}
                                </div>

                                <div className={"student-progress__progress"}>
                                    <span>{`${saneValue}/${itemNumber}`}</span>
                                    <ProgressBar
                                        className={
                                            "student-progress__progress-bar"
                                        }
                                        value={saneValue}
                                        maxValue={itemNumber}
                                        height={8}
                                        colors={{
                                            foreground:
                                                "var(--element-teacher-default)",
                                            background: "var(--element-grey10)",
                                        }}
                                    />
                                </div>
                            </li>
                        );
                    }
                )}
            </ul>
        </div>
    );
};

const messages = defineMessages({
    experimentationPreviewExercises: {
        id: "experimentation-preview-exercises",
        defaultMessage:
            "{count, plural, =0 {No exercise} =1 {1 exercise} other {# exercises}}",
    },
    experimentationPreviewProgressions: {
        id: "experimentation-preview-progressions",
        defaultMessage: "Progressions",
    },
    selectClassroom: {
        id: "experimentation-preview-select-classroom",
        defaultMessage: "Classes",
    },
    preTests: {
        id: "experimentation-pre-tests",
        defaultMessage: "Pre-tests",
    },
    postTests: {
        id: "experimentation-post-tests",
        defaultMessage: "Post-tests",
    },
    experimentationListError: {
        id: "experimentation-list-teachers-loading-error",
        defaultMessage:
            "An error occurred when loading the progression data on the tests.",
    },
    experimentationClosedPreTests: {
        id: "experimentation-list-teachers-ClosedPreTests",
        defaultMessage:
            "The pre-tests are closed! Train your students until the opening of the post-tests.",
    },
    experimentationTestsIncoming: {
        id: "experimentation-list-teachers-TestsIncoming",
        defaultMessage:
            "You will be able to follow the progress of your students when the tests are available",
    },
    experimentationPreTestsClosed: {
        id: "experimentation-list-teachers-PreTestsClosed",
        defaultMessage:
            "The pre-tests are closed! Train your students until the opening of the post-tests.",
    },
    experimentationAllTestsAreClosed: {
        id: "experimentation-list-teachers-AllTestsAreClosed",
        defaultMessage:
            "All tests are closed! Thank you for your participation.",
    },
});
